import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import MessagesReceived from "./MessagesReceived";
import MessagesSent from "./MessagesSent";
import GroupMessages from "./GroupMessages";
import CreateMessageForm from "./CreateMessageForm";
import Button from "../../../components/Button/Button";
import { LuMessageSquarePlus } from "react-icons/lu";
import { FaInbox, FaPaperPlane, FaUsers } from "react-icons/fa";
import useScrollToTop from "../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

/* -----------------------------
   Petites briques UI locales
----------------------------- */

function Badge({ value, tone = "violet" }) {
  if (!value || value <= 0) return null;
  const toneMap = {
    violet: "bg-violet-600",
    amber: "bg-amber-600",
    sky: "bg-sky-600",
    rose: "bg-rose-600",
    slate: "bg-slate-600",
    emerald: "bg-emerald-600",
  };
  const bg = toneMap[tone] || toneMap.violet;
  return (
    <span
      className={`${bg} text-white text-[11px] font-bold rounded-full px-2 py-[2px] leading-none`}
    >
      {value}
    </span>
  );
}

function ModuleNav({ items, activeId, onChange }) {
  return (
    <>
      {/* Mobile: pills */}
      <div className="md:hidden -mx-2 px-2 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 pb-2">
          {items.map((it) => {
            const Active = activeId === it.id;
            const Icon = it.icon;
            return (
              <button
                key={it.id}
                onClick={() => onChange(it.id)}
                className={`whitespace-nowrap inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm ring-1 transition ${
                  Active
                    ? "bg-violet-600 text-white ring-violet-500"
                    : "bg-white/80 dark:bg-slate-900/70 text-slate-700 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-white"
                }`}
                title={it.title}
              >
                <Icon />
                {it.title}
                <Badge value={it.badge} tone={it.badgeTone} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop: sidebar */}
      <aside className="hidden md:block md:w-64">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          <div className="px-4 py-3 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <p className="font-semibold">Messagerie</p>
          </div>
          <nav className="p-2">
            {items.map((it) => {
              const Active = activeId === it.id;
              const Icon = it.icon;
              return (
                <button
                  key={it.id}
                  onClick={() => onChange(it.id)}
                  className={`w-full text-left flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm ring-1 transition mb-2 ${
                    Active
                      ? "bg-violet-600 text-white ring-violet-500"
                      : "bg-white/80 dark:bg-slate-900/70 text-slate-700 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-white"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Icon />
                    {it.title}
                  </span>
                  <Badge value={it.badge} tone={it.badgeTone} />
                </button>
              );
            })}
          </nav>
        </div>
      </aside>
    </>
  );
}

function SectionShell({ title, subtitle, toolbar, children }) {
  return (
    <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
      <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h4 className="text-lg sm:text-xl font-semibold">{title}</h4>
            {subtitle && <p className="text-white/90 text-sm">{subtitle}</p>}
          </div>
          {toolbar}
        </div>
      </div>
      <div className="p-4 sm:p-6">{children}</div>
    </div>
  );
}

/* -----------------------------
   Page principale
----------------------------- */

const Messaging = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;
  const isAdminOrTeacher = ["enseignant", "admin"].includes(userRole);

  // Onglet actif
  const [active, setActive] = useState("received");

  // Affichage du formulaire
  const [showForm, setShowForm] = useState(false);
  const [refreshMessages, setRefreshMessages] = useState(false);

  // Compteurs (pour badges)
  const [counts, setCounts] = useState({
    received: { total: 0, unread: 0 },
    sent: { total: 0 },
    group: { total: 0 }, // si besoin
  });

  const setModuleCounts = useCallback((key, obj) => {
    setCounts((c) => ({ ...c, [key]: { ...c[key], ...obj } }));
  }, []);

  const handleCountsReceived = useCallback(
    (obj) => setModuleCounts("received", obj),
    [setModuleCounts]
  );
  const handleCountsSent = useCallback(
    (obj) => setModuleCounts("sent", obj),
    [setModuleCounts]
  );
  const handleCountsGroup = useCallback(
    (obj) => setModuleCounts("group", obj),
    [setModuleCounts]
  );

  const modules = useMemo(
    () =>
      [
        {
          id: "received",
          title: "Reçus",
          icon: FaInbox,
          badge: counts.received.unread || 0,
          badgeTone: "violet",
        },
        {
          id: "sent",
          title: "Envoyés",
          icon: FaPaperPlane,
          badge: 0,
        },
        isAdminOrTeacher && {
          id: "group",
          title: "Groupé",
          icon: FaUsers,
          badge: 0,
        },
      ].filter(Boolean),
    [counts, isAdminOrTeacher]
  );

  const handleNewMessage = () => setShowForm(true);
  const handleSuccess = () => {
    setShowForm(false);
    setRefreshMessages(true);
  };
  const hideForm = () => setShowForm(false);

  useEffect(() => {
    if (!userId) {
      toast.error("Vous devez être connecté pour accéder à la messagerie.");
    }
  }, [userId]);

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Utilisateur"
          title="Messagerie"
          align="center"
          className="mb-1"
        />

        {/* Nav dashboard */}
        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Layout : sidebar + contenu */}
        <div className="grid grid-cols-1 md:grid-cols-[16rem,1fr] gap-4 md:gap-6">
          <ModuleNav items={modules} activeId={active} onChange={setActive} />

          <div className="space-y-6">
            {/* Barre d’outils commune */}
            <div className="flex items-center justify-end">
              <Button
                text="Nouveau message"
                onClick={handleNewMessage}
                icon={LuMessageSquarePlus}
                className="gap-2 !rounded-xl"
                hoverLabel="Composer un message"
              />
            </div>

            {/* Formulaire de création (au-dessus du contenu) */}
            {showForm && (
              <SectionShell
                title="Nouveau message"
                subtitle="Rédigez et envoyez un message privé."
              >
                <CreateMessageForm
                  userId={userId}
                  userRole={userRole}
                  onSuccess={handleSuccess}
                  show={showForm}
                  onHide={hideForm}
                />
              </SectionShell>
            )}

            {/* Reçus */}
            {active === "received" && (
              <SectionShell
                title="Messages reçus"
                subtitle="Boîte de réception."
              >
                <MessagesReceived
                  userId={userId}
                  refresh={refreshMessages}
                  setRefresh={setRefreshMessages}
                  onCounts={handleCountsReceived} // ⬅️ stable maintenant
                />
              </SectionShell>
            )}

            {/* Envoyés */}
            {active === "sent" && (
              <SectionShell
                title="Messages envoyés"
                subtitle="Historique des messages que vous avez envoyés."
              >
                <MessagesSent
                  userId={userId}
                  refresh={refreshMessages}
                  setRefresh={setRefreshMessages}
                  onCounts={handleCountsSent} // ⬅️ stable
                />
              </SectionShell>
            )}

            {/* Groupé (enseignants/admin) */}
            {active === "group" && isAdminOrTeacher && (
              <SectionShell
                title="Message groupé"
                subtitle="Envoyez un message à un ensemble de destinataires."
              >
                <GroupMessages
                  userId={userId}
                  onCounts={handleCountsGroup} // ⬅️ stable
                />
              </SectionShell>
            )}
          </div>
        </div>
      </div>

      <ToastContainer />
    </section>
  );
};

export default Messaging;
